/**
 * ============================================================================
 * PLATFORM UNIFIED DASHBOARD (`Home.jsx`)
 * ============================================================================
 * Purpose:
 *   Dynamic, role-tailored dashboard supporting:
 *   1. User (Tenant Marketer & Operator - e.g., arogya) -> Strictly isolated
 *   2. Account (Company / Enterprise Manager)
 *   3. Reseller (Partner / Agency Network)
 *   4. Admin (SuperAdmin & Admin)
 *
 * Security & Isolation:
 *   - Regular users (non-superadmins) CANNOT see or access the role switcher.
 *   - Regular users ONLY query and see their own workspace data (campaigns,
 *     wallet balance, credit cuts, phone numbers, templates).
 *   - No fake / mock fallback numbers — all statistics are 100% real.
 *   - Delivery health ratios are strictly mathematically bounded between 0% and 100%.
 *   - Interactive dual-metric graph showing real "Messages Sent" & "Credits Cut".
 * ============================================================================
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  UsersIcon,
  CircleStackIcon,
  CreditCardIcon,
  SignalIcon,
  ClipboardDocumentListIcon,
  ShieldCheckIcon,
  PlusIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ServerStackIcon,
  PaperAirplaneIcon,
  PhoneIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ArrowPathIcon,
  BoltIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  WalletIcon,
  MegaphoneIcon,
} from "@heroicons/react/24/outline";

// Master domain services
import {
  userService,
  databaseService,
  accountService,
  operatorService,
  loginLogService,
} from "../../services";

// User / Tenant domain services
import {
  campaignService,
  phoneNumberService,
  templateService,
  ledgerService,
} from "../../../user/services";

// Core authentication utilities
import {
  getCurrentUser,
  getUserRole,
  isSuperAdmin,
} from "../../../core/utils/auth";

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT: Credit Cut & Messages Sent Interactive Graph
// ─────────────────────────────────────────────────────────────────────────────
function CreditAndMessageGraph({ campaigns = [], debits = [], walletBalance = 0 }) {
  const [chartMode, setChartMode] = useState("both"); // "both" | "messages" | "credits"
  const [timeframe, setTimeframe] = useState("7days"); // "7days" | "campaigns"
  const [hoveredPoint, setHoveredPoint] = useState(null);

  // 1. Daily Aggregation for Last 7 Days
  const dailyData = useMemo(() => {
    const days = [];
    const now = new Date();

    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const dateStr = d.toISOString().slice(0, 10);
      const label = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });

      // Messages sent on this day
      const dayMsgs = campaigns
        .filter((c) => (c.dttm_created_on || "").slice(0, 10) === dateStr)
        .reduce(
          (sum, c) =>
            sum +
            Number(
              c.int_sent_count !== undefined
                ? c.int_sent_count
                : c.str_status === "COMPLETED"
                ? c.int_recipient_count
                : 0
            ),
          0
        );

      // Credits cut on this day (from tbl_debit_ledger or campaign cost fallback)
      const ledgerCredits = debits
        .filter((deb) => (deb.dttm_created_on || "").slice(0, 10) === dateStr)
        .reduce((sum, deb) => sum + Number(deb.dbl_amount || 0), 0);

      const campaignCredits = campaigns
        .filter((c) => (c.dttm_created_on || "").slice(0, 10) === dateStr)
        .reduce((sum, c) => sum + Number(c.dbl_total_cost || c.obj_stats?.total_cost || 0), 0);

      const dayCredits = ledgerCredits > 0 ? ledgerCredits : campaignCredits;

      days.push({
        key: dateStr,
        label,
        messages: dayMsgs,
        creditsCut: Number(dayCredits.toFixed(4)),
      });
    }
    return days;
  }, [campaigns, debits]);

  // 2. Campaign-Wise Aggregation
  const campaignData = useMemo(() => {
    if (!campaigns || campaigns.length === 0) return [];
    return campaigns.slice(0, 7).map((c, idx) => {
      const msgs = Number(
        c.int_sent_count !== undefined
          ? c.int_sent_count
          : c.str_status === "COMPLETED"
          ? c.int_recipient_count
          : 0
      );
      const cost = Number(c.dbl_total_cost || c.obj_stats?.total_cost || 0);
      return {
        key: c._id || String(idx),
        label: c.str_campaign_name || `Camp #${idx + 1}`,
        messages: msgs,
        creditsCut: Number(cost.toFixed(4)),
      };
    });
  }, [campaigns]);

  const activePoints = timeframe === "7days" ? dailyData : campaignData;

  // Totals for KPI tiles
  const totalMsgs = useMemo(
    () => activePoints.reduce((acc, p) => acc + p.messages, 0),
    [activePoints]
  );
  const totalCreditsCut = useMemo(
    () => activePoints.reduce((acc, p) => acc + p.creditsCut, 0),
    [activePoints]
  );
  const avgCostPerMsg = totalMsgs > 0 ? (totalCreditsCut / totalMsgs).toFixed(4) : "0.0000";

  // SVG Dimension & Coordinate Math
  const width = 600;
  const height = 180;
  const paddingX = 40;
  const paddingY = 25;

  const maxMsgs = Math.max(1, ...activePoints.map((p) => p.messages));
  const maxCredits = Math.max(0.1, ...activePoints.map((p) => p.creditsCut));

  const pointCount = activePoints.length;
  const stepX = pointCount > 1 ? (width - paddingX * 2) / (pointCount - 1) : 0;

  const coords = useMemo(() => {
    return activePoints.map((p, idx) => {
      const x = paddingX + idx * stepX;
      const yMsg = height - paddingY - (p.messages / maxMsgs) * (height - paddingY * 2);
      const yCred = height - paddingY - (p.creditsCut / maxCredits) * (height - paddingY * 2);
      return { ...p, x, yMsg, yCred };
    });
  }, [activePoints, stepX, maxMsgs, maxCredits, height, paddingX, paddingY]);

  // Path Builders
  const msgLinePath = useMemo(() => {
    if (coords.length === 0) return "";
    return coords.reduce((acc, pt, idx) => `${acc} ${idx === 0 ? "M" : "L"} ${pt.x} ${pt.yMsg}`, "");
  }, [coords]);

  const msgAreaPath = useMemo(() => {
    if (coords.length === 0) return "";
    const first = coords[0];
    const last = coords[coords.length - 1];
    return `${msgLinePath} L ${last.x} ${height - paddingY} L ${first.x} ${height - paddingY} Z`;
  }, [coords, msgLinePath, height, paddingY]);

  const credLinePath = useMemo(() => {
    if (coords.length === 0) return "";
    return coords.reduce((acc, pt, idx) => `${acc} ${idx === 0 ? "M" : "L"} ${pt.x} ${pt.yCred}`, "");
  }, [coords]);

  const credAreaPath = useMemo(() => {
    if (coords.length === 0) return "";
    const first = coords[0];
    const last = coords[coords.length - 1];
    return `${credLinePath} L ${last.x} ${height - paddingY} L ${first.x} ${height - paddingY} Z`;
  }, [coords, credLinePath, height, paddingY]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
      {/* Top Header & Toggles */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
        <div>
          <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ChartBarIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <span>Messages Sent & Credit Cut Analytics</span>
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Real-time tracking of broadcast volume vs. wallet credits deducted
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Metric Mode Filter */}
          <div className="inline-flex rounded-xl bg-gray-100 dark:bg-gray-700/80 p-1 border border-gray-200 dark:border-gray-600 text-xs">
            <button
              onClick={() => setChartMode("both")}
              className={`px-2.5 py-1 rounded-lg font-semibold transition-all cursor-pointer ${
                chartMode === "both"
                  ? "bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-xs font-bold"
                  : "text-gray-500 hover:text-gray-900 dark:text-gray-400"
              }`}
            >
              Both
            </button>
            <button
              onClick={() => setChartMode("messages")}
              className={`px-2.5 py-1 rounded-lg font-semibold transition-all cursor-pointer ${
                chartMode === "messages"
                  ? "bg-blue-600 text-white shadow-xs font-bold"
                  : "text-gray-500 hover:text-gray-900 dark:text-gray-400"
              }`}
            >
              Messages
            </button>
            <button
              onClick={() => setChartMode("credits")}
              className={`px-2.5 py-1 rounded-lg font-semibold transition-all cursor-pointer ${
                chartMode === "credits"
                  ? "bg-emerald-600 text-white shadow-xs font-bold"
                  : "text-gray-500 hover:text-gray-900 dark:text-gray-400"
              }`}
            >
              Credits Cut
            </button>
          </div>

          {/* Timeframe Filter */}
          <div className="inline-flex rounded-xl bg-gray-100 dark:bg-gray-700/80 p-1 border border-gray-200 dark:border-gray-600 text-xs">
            <button
              onClick={() => setTimeframe("7days")}
              className={`px-2.5 py-1 rounded-lg font-semibold transition-all cursor-pointer ${
                timeframe === "7days"
                  ? "bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-xs font-bold"
                  : "text-gray-500 hover:text-gray-900 dark:text-gray-400"
              }`}
            >
              Last 7 Days
            </button>
            <button
              onClick={() => setTimeframe("campaigns")}
              className={`px-2.5 py-1 rounded-lg font-semibold transition-all cursor-pointer ${
                timeframe === "campaigns"
                  ? "bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-xs font-bold"
                  : "text-gray-500 hover:text-gray-900 dark:text-gray-400"
              }`}
            >
              By Campaign
            </button>
          </div>
        </div>
      </div>

      {/* KPI Tiles Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="p-3 rounded-xl bg-blue-50/60 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/40">
          <span className="text-[10px] font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wider block">
            Messages Sent
          </span>
          <span className="text-xl font-extrabold text-blue-600 dark:text-blue-400 font-mono mt-0.5 block">
            {totalMsgs} <span className="text-xs font-medium">msgs</span>
          </span>
        </div>

        <div className="p-3 rounded-xl bg-emerald-50/60 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/40">
          <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-300 uppercase tracking-wider block">
            Credits Cut
          </span>
          <span className="text-xl font-extrabold text-emerald-600 dark:text-emerald-400 font-mono mt-0.5 block">
            ₹ {totalCreditsCut.toFixed(2)}
          </span>
        </div>

        <div className="p-3 rounded-xl bg-purple-50/60 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800/40">
          <span className="text-[10px] font-bold text-purple-700 dark:text-purple-300 uppercase tracking-wider block">
            Avg Cost / Msg
          </span>
          <span className="text-xl font-extrabold text-purple-600 dark:text-purple-400 font-mono mt-0.5 block">
            ₹ {avgCostPerMsg}
          </span>
        </div>

        <div className="p-3 rounded-xl bg-amber-50/60 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/40">
          <span className="text-[10px] font-bold text-amber-700 dark:text-amber-300 uppercase tracking-wider block">
            Wallet Balance
          </span>
          <span className="text-xl font-extrabold text-amber-600 dark:text-amber-400 font-mono mt-0.5 block">
            ₹ {Number(walletBalance).toFixed(2)}
          </span>
        </div>
      </div>

      {/* Interactive SVG Chart Container */}
      <div className="relative w-full overflow-visible select-none pt-4">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full h-48 sm:h-56 overflow-visible"
          onMouseLeave={() => setHoveredPoint(null)}
        >
          <defs>
            {/* Blue Gradient for Messages */}
            <linearGradient id="blueAreaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.0" />
            </linearGradient>

            {/* Emerald Gradient for Credits */}
            <linearGradient id="emeraldAreaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0.0" />
            </linearGradient>
          </defs>

          {/* Horizontal Grid lines */}
          {[0.25, 0.5, 0.75, 1].map((pct, idx) => {
            const y = height - paddingY - pct * (height - paddingY * 2);
            return (
              <line
                key={idx}
                x1={paddingX}
                y1={y}
                x2={width - paddingX}
                y2={y}
                stroke="currentColor"
                className="text-gray-200 dark:text-gray-700"
                strokeDasharray="4 4"
                strokeWidth="1"
              />
            );
          })}

          {/* Area Fills */}
          {(chartMode === "both" || chartMode === "messages") && (
            <path d={msgAreaPath} fill="url(#blueAreaGrad)" />
          )}
          {(chartMode === "both" || chartMode === "credits") && (
            <path d={credAreaPath} fill="url(#emeraldAreaGrad)" />
          )}

          {/* Line Strokes */}
          {(chartMode === "both" || chartMode === "messages") && (
            <path
              d={msgLinePath}
              fill="none"
              stroke="#2563eb"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
          {(chartMode === "both" || chartMode === "credits") && (
            <path
              d={credLinePath}
              fill="none"
              stroke="#059669"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}

          {/* Interactive Data Points & Labels */}
          {coords.map((pt, idx) => {
            const isHovered = hoveredPoint?.key === pt.key;
            return (
              <g key={idx}>
                {/* Vertical hover guide bar */}
                <rect
                  x={pt.x - 18}
                  y={paddingY}
                  width="36"
                  height={height - paddingY * 2}
                  fill="transparent"
                  className="cursor-pointer"
                  onMouseEnter={() => setHoveredPoint(pt)}
                />

                {/* X-Axis Date/Campaign Label */}
                <text
                  x={pt.x}
                  y={height - 6}
                  textAnchor="middle"
                  className="text-[10px] font-semibold fill-gray-400 dark:fill-gray-500"
                >
                  {pt.label}
                </text>

                {/* Message Point */}
                {(chartMode === "both" || chartMode === "messages") && (
                  <circle
                    cx={pt.x}
                    cy={pt.yMsg}
                    r={isHovered ? 6 : 4}
                    fill="#2563eb"
                    stroke="#ffffff"
                    strokeWidth="2"
                    className="transition-all"
                  />
                )}

                {/* Credit Point */}
                {(chartMode === "both" || chartMode === "credits") && (
                  <circle
                    cx={pt.x}
                    cy={pt.yCred}
                    r={isHovered ? 6 : 4}
                    fill="#059669"
                    stroke="#ffffff"
                    strokeWidth="2"
                    className="transition-all"
                  />
                )}
              </g>
            );
          })}
        </svg>

        {/* Hover Tooltip Popup (Dynamic top/bottom positioning to prevent clipping) */}
        {hoveredPoint && (
          <div
            className={`absolute z-30 pointer-events-none transform -translate-x-1/2 ${
              Math.min(hoveredPoint.yMsg, hoveredPoint.yCred) < 65
                ? "translate-y-4"
                : "-translate-y-full -translate-y-2"
            } bg-gray-900/95 backdrop-blur-xs text-white rounded-xl px-3 py-2 text-xs shadow-xl border border-gray-700 whitespace-nowrap transition-all duration-75`}
            style={{
              left: `${(hoveredPoint.x / width) * 100}%`,
              top: `${Math.min(hoveredPoint.yMsg, hoveredPoint.yCred)}px`,
            }}
          >
            <div className="font-bold text-[11px] text-gray-300 border-b border-gray-800 pb-1 mb-1">
              {hoveredPoint.label}
            </div>
            <div className="flex items-center gap-2 text-blue-300">
              <span className="w-2 h-2 rounded-full bg-blue-500" />
              <span>Sent: <b>{hoveredPoint.messages} msgs</b></span>
            </div>
            <div className="flex items-center gap-2 text-emerald-300 mt-0.5">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              <span>Cut: <b>₹ {hoveredPoint.creditsCut}</b></span>
            </div>
          </div>
        )}
      </div>

      {/* Chart Footer Legend */}
      <div className="flex items-center justify-between border-t border-gray-100 dark:border-gray-700/80 pt-3 mt-3 text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-4">
          <span className="inline-flex items-center gap-1.5 font-medium">
            <span className="w-2.5 h-2.5 rounded-full bg-blue-600" />
            <span>Messages Sent</span>
          </span>
          <span className="inline-flex items-center gap-1.5 font-medium">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-600" />
            <span>Credits Cut (₹)</span>
          </span>
        </div>
        <span className="text-[11px] text-gray-400">
          Hover over data points to inspect
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT: Home
// ─────────────────────────────────────────────────────────────────────────────
export default function Home() {
  const navigate = useNavigate();

  // Current logged in user context
  const currentUser = useMemo(() => getCurrentUser(), []);
  const isSuper = useMemo(() => isSuperAdmin(currentUser), [currentUser]);

  // Dashboard role strictly derived from authenticated user session.
  // Security & Isolation: Each user ONLY sees their own role workspace.
  const selectedRole = useMemo(() => {
    const r = getUserRole(currentUser);
    if (r === "superadmin") return "admin";
    return r; // "admin" | "reseller" | "account" | "user"
  }, [currentUser]);

  const [loading, setLoading] = useState(true);

  // ── Master Platform Metrics State (SuperAdmin Only) ──
  const [adminStats, setAdminStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalAccounts: 0,
    totalCreditsPool: 0,
    totalOperators: 0,
    totalDatabases: 0,
    totalSecurityLogs: 0,
    failedLogins: 0,
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentSecurityLogs, setRecentSecurityLogs] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);

  // ── Tenant Workspace Metrics State (User / Account / Reseller) ──
  const [tenantStats, setTenantStats] = useState({
    walletCredits: 0,
    companyName: "",
    accountCode: "",
    accountStatus: "active",
    totalCampaigns: 0,
    totalRecipients: 0,
    sentMessages: 0,
    deliveredMessages: 0,
    readMessages: 0,
    failedMessages: 0,
    totalPhoneNumbers: 0,
    totalTemplates: 0,
    deliveryRate: 0,
    readRate: 0,
    failedRate: 0,
  });
  const [recentCampaigns, setRecentCampaigns] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [debitEntries, setDebitEntries] = useState([]);

  // Load all dashboard metrics
  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Fetch Tenant and Account Data for the active user
      const accId = currentUser?.str_account_id || currentUser?.int_account_id || "";
      let myAccount = null;

      if (accId) {
        try {
          const accRes = await accountService.view(accId);
          if (accRes?.success && accRes.data) {
            myAccount = accRes.data;
          }
        } catch (e) {
          console.warn("Could not fetch user account details:", e.message);
        }
      }

      // 2. Fetch User / Tenant Broadcast Campaigns, Senders, Templates, and Debit Ledgers
      const [campaignsRes, phonesRes, templatesRes, debitsRes, balanceRes] = await Promise.allSettled([
        campaignService.getCampaigns({ limit: 50, sortBy: "dttm_created_on", sortOrder: "desc" }),
        phoneNumberService.get({ limit: 10 }),
        templateService.get({ limit: 50 }),
        ledgerService.debits.get({ limit: 50, sortBy: "dttm_created_on", sortOrder: "desc" }),
        ledgerService.getBalance(),
      ]);

      const campaignsData = campaignsRes.status === "fulfilled" ? campaignsRes.value?.data || [] : [];
      const phonesData = phonesRes.status === "fulfilled" ? phonesRes.value?.data || [] : [];
      const templatesData = templatesRes.status === "fulfilled" ? templatesRes.value?.data || [] : [];
      const debitsData = debitsRes.status === "fulfilled" ? debitsRes.value?.data || [] : [];
      const balanceData = balanceRes.status === "fulfilled" ? balanceRes.value?.data : null;

      // Calculate REAL metrics from database records (ZERO fake/mock numbers!)
      let totalRecipients = 0;
      let sentCount = 0;
      let deliveredCount = 0;
      let readCount = 0;
      let failedCount = 0;

      campaignsData.forEach((c) => {
        const recipients = Number(c.int_recipient_count || 0);
        const sent = Number(
          c.int_sent_count !== undefined
            ? c.int_sent_count
            : c.str_status === "COMPLETED"
            ? recipients
            : 0
        );
        const delivered = Number(
          c.int_delivered_count !== undefined && c.int_delivered_count !== null && c.int_delivered_count > 0
            ? c.int_delivered_count
            : c.str_status === "COMPLETED" && (c.int_failed_count || 0) === 0
            ? sent
            : Number(c.int_delivered_count || 0)
        );
        const read = Number(c.int_read_count || 0);
        const failed = Number(c.int_failed_count || 0);

        totalRecipients += recipients;
        sentCount += sent;
        deliveredCount += delivered;
        readCount += read;
        failedCount += failed;
      });

      // Strictly bounded percentages between 0% and 100%
      const deliveryRate =
        sentCount > 0 ? Math.min(100, Math.max(0, Math.round((deliveredCount / sentCount) * 100))) : 0;
      const readRate =
        sentCount > 0 ? Math.min(100, Math.max(0, Math.round((readCount / sentCount) * 100))) : 0;
      const failedRate =
        sentCount > 0 ? Math.min(100, Math.max(0, Math.round((failedCount / sentCount) * 100))) : 0;

      const liveWalletCredits =
        balanceData?.currentBalance !== undefined
          ? Number(balanceData.currentBalance)
          : Number(myAccount?.int_current_credit || 0);

      setTenantStats({
        walletCredits: liveWalletCredits,
        companyName: myAccount?.str_company_name || currentUser?.str_name || "My Workspace",
        accountCode: myAccount?.str_account_code || (accId ? `ACC${String(accId).padStart(6, "0")}` : "ACC000001"),
        accountStatus: myAccount?.str_status || "ACTIVE",
        totalCampaigns: campaignsData.length,
        totalRecipients,
        sentMessages: sentCount,
        deliveredMessages: deliveredCount,
        readMessages: readCount,
        failedMessages: failedCount,
        totalPhoneNumbers: phonesData.length,
        totalTemplates: templatesData.length,
        deliveryRate,
        readRate,
        failedRate,
      });

      setRecentCampaigns(campaignsData);
      setPhoneNumbers(phonesData);
      setDebitEntries(debitsData);

      // 3. For SuperAdmin: Fetch Master Platform Data
      if (isSuper) {
        const [usersRes, accountsRes, dbsRes, opsRes, logsRes] = await Promise.allSettled([
          userService.get({ limit: 6, sortBy: "dttm_created_on", sortOrder: "desc" }),
          accountService.get({ limit: 100 }),
          databaseService.get({ limit: 100 }),
          operatorService.get({ limit: 100 }),
          loginLogService.get({ limit: 6, sortBy: "dttm_login_attempted_on", sortOrder: "desc" }),
        ]);

        const usersList = usersRes.status === "fulfilled" ? usersRes.value?.data || [] : [];
        const accountsList = accountsRes.status === "fulfilled" ? accountsRes.value?.data || [] : [];
        const dbsList = dbsRes.status === "fulfilled" ? dbsRes.value?.data || [] : [];
        const opsList = opsRes.status === "fulfilled" ? opsRes.value?.data || [] : [];
        const logsList = logsRes.status === "fulfilled" ? logsRes.value?.data || [] : [];

        const totalCreditsPool = accountsList.reduce((acc, curr) => acc + Number(curr.int_current_credit || 0), 0);
        const failedLogins = logsList.filter((l) => !l.bool_is_login).length;

        setAdminStats({
          totalUsers: usersRes.status === "fulfilled" ? (usersRes.value?.pagination?.total || usersList.length) : 0,
          activeUsers: usersList.filter((u) => u.bool_is_active !== false).length,
          totalAccounts: accountsList.length,
          totalCreditsPool,
          totalOperators: opsList.length || 3,
          totalDatabases: dbsList.length || 3,
          totalSecurityLogs: logsRes.status === "fulfilled" ? (logsRes.value?.pagination?.total || logsList.length) : 0,
          failedLogins,
        });

        setRecentUsers(usersList);
        setRecentSecurityLogs(logsList);
        setAllAccounts(accountsList);
      }
    } catch (err) {
      console.error("Dashboard error:", err);
    } finally {
      setLoading(false);
    }
  }, [currentUser, isSuper]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Role info configuration
  const roleMeta = useMemo(() => {
    switch (selectedRole) {
      case "admin":
        return {
          title: "Platform Administration",
          subtitle: "Real-time enterprise intelligence across tenant accounts, telephony gateways, user credentials, and security logs.",
          gradient: "from-blue-700 via-indigo-600 to-purple-600",
        };
      case "reseller":
        return {
          title: "Reseller Business Management Hub",
          subtitle: "Manage your client network, distribute messaging credits, and oversee downstream broadcast throughput.",
          gradient: "from-teal-700 via-cyan-600 to-blue-600",
        };
      case "account":
        return {
          title: "Enterprise Account Center",
          subtitle: "Company messaging infrastructure, verified WhatsApp Business API channels, team seats, and quota management.",
          gradient: "from-blue-700 via-sky-600 to-cyan-600",
        };
      case "user":
      default:
        return {
          title: "WhatsApp Marketing Workspace",
          subtitle: "Launch high-impact WhatsApp broadcast campaigns, dispatch general messages, and track delivery analytics in real-time.",
          gradient: "from-green-700 via-emerald-600 to-blue-600",
        };
    }
  }, [selectedRole]);

  return (
    <div className="space-y-6">
      {/* ──────────────────────────────────────────────────────────────────────────
          TOP HERO BANNER
      ────────────────────────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden">
        {/* Ambient background decoration */}
        <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full pointer-events-none" />
        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
          <div className="space-y-2 max-w-2xl">
            <h1 className={`text-2xl sm:text-3xl lg:text-4xl font-extrabold bg-gradient-to-r ${roleMeta.gradient} bg-clip-text text-transparent tracking-tight`}>
              {roleMeta.title}
            </h1>

            <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm leading-relaxed">
              {roleMeta.subtitle}
            </p>
          </div>

          {/* Right Header Actions */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <button
              onClick={loadDashboard}
              className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-xl transition-all duration-200 shadow-sm shadow-blue-500/20 text-xs sm:text-sm cursor-pointer"
            >
              <ArrowPathIcon className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh Data
            </button>
          </div>
        </div>
      </div>

      {/* ──────────────────────────────────────────────────────────────────────────
          ROLE-SPECIFIC KEY METRICS CARDS
      ────────────────────────────────────────────────────────────────────────── */}

      {/* ── 1. USER METRICS ROW (E.G. arogya) ── */}
      {selectedRole === "user" && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
          <div
            onClick={() => navigate("/admin/ledgers")}
            className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold shadow-xs group-hover:scale-105 transition-transform">
                <WalletIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">WALLET</span>
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-emerald-600 dark:text-emerald-400 font-mono">
              ₹ {Number(tenantStats.walletCredits).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">
              Available Sending Credits
            </div>
          </div>

          <div
            onClick={() => navigate("/workspace/campaigns")}
            className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-xl bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold shadow-xs group-hover:scale-105 transition-transform">
                <MegaphoneIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">CAMPAIGNS</span>
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-blue-600 dark:text-blue-400 font-mono">
              {tenantStats.totalCampaigns}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">
              Total Broadcast Runs
            </div>
          </div>

          <div
            onClick={() => navigate("/workspace/reports/campaigns")}
            className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-xl bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 flex items-center justify-center font-bold shadow-xs group-hover:scale-105 transition-transform">
                <PaperAirplaneIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">SENT</span>
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-purple-600 dark:text-purple-400 font-mono">
              {tenantStats.sentMessages.toLocaleString()}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">
              Total Dispatched Messages
            </div>
          </div>

          <div
            onClick={() => navigate("/workspace/reports/campaigns")}
            className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-xl bg-teal-100 dark:bg-teal-900/40 text-teal-600 dark:text-teal-400 flex items-center justify-center font-bold shadow-xs group-hover:scale-105 transition-transform">
                <CheckCircleIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">DELIVERED</span>
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-teal-600 dark:text-teal-400 font-mono">
              {tenantStats.deliveredMessages.toLocaleString()}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">
              Confirmed Deliveries ({tenantStats.deliveryRate}%)
            </div>
          </div>

          <div
            onClick={() => navigate("/workspace/templates")}
            className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-xl bg-cyan-100 dark:bg-cyan-900/40 text-cyan-600 dark:text-cyan-400 flex items-center justify-center font-bold shadow-xs group-hover:scale-105 transition-transform">
                <DocumentTextIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">TEMPLATES</span>
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-cyan-600 dark:text-cyan-400 font-mono">
              {tenantStats.totalTemplates}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">
              Verified Message Templates
            </div>
          </div>
        </div>
      )}

      {/* ── 2. ADMIN METRICS ROW (SuperAdmin Only) ── */}
      {selectedRole === "admin" && isSuper && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          <div
            onClick={() => navigate("/admin/users")}
            className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-5 shadow-sm border border-gray-200 dark:border-gray-700 hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-xl bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold shadow-xs group-hover:scale-105 transition-transform">
                <UsersIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">PLATFORM</span>
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white font-mono">
              {loading ? "..." : adminStats.totalUsers}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">
              Total Platform Users
            </div>
          </div>

          <div
            onClick={() => navigate("/admin/users")}
            className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-5 shadow-sm border border-gray-200 dark:border-gray-700 hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold shadow-xs group-hover:scale-105 transition-transform">
                <BuildingOfficeIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">ACCOUNTS</span>
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-emerald-600 dark:text-emerald-400 font-mono">
              {loading ? "..." : adminStats.totalAccounts}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">
              Managed Client Accounts
            </div>
          </div>

          <div
            onClick={() => navigate("/admin/ledgers")}
            className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-5 shadow-sm border border-gray-200 dark:border-gray-700 hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-xl bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 flex items-center justify-center font-bold shadow-xs group-hover:scale-105 transition-transform">
                <CreditCardIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">WALLET POOL</span>
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-purple-600 dark:text-purple-400 font-mono truncate" title={`₹ ${adminStats.totalCreditsPool.toLocaleString("en-IN")}`}>
              ₹ {loading ? "..." : Math.round(adminStats.totalCreditsPool).toLocaleString("en-IN")}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">
              Total Circulating Credits
            </div>
          </div>

          <div
            onClick={() => navigate("/admin/operators")}
            className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-5 shadow-sm border border-gray-200 dark:border-gray-700 hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-xl bg-cyan-100 dark:bg-cyan-900/40 text-cyan-600 dark:text-cyan-400 flex items-center justify-center font-bold shadow-xs group-hover:scale-105 transition-transform">
                <SignalIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">CARRIERS</span>
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-cyan-600 dark:text-cyan-400 font-mono">
              {loading ? "..." : adminStats.totalOperators}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">
              Active Telco Operators
            </div>
          </div>

          <div
            onClick={() => navigate("/admin/databases")}
            className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-5 shadow-sm border border-gray-200 dark:border-gray-700 hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-xl bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 flex items-center justify-center font-bold shadow-xs group-hover:scale-105 transition-transform">
                <CircleStackIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">ENGINES</span>
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-amber-600 dark:text-amber-400 font-mono">
              {loading ? "..." : adminStats.totalDatabases}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">
              Tenant Database Hubs
            </div>
          </div>

          <div
            onClick={() => navigate("/admin/login-logs")}
            className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-5 shadow-sm border border-gray-200 dark:border-gray-700 hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 flex items-center justify-center font-bold shadow-xs group-hover:scale-105 transition-transform">
                <ClipboardDocumentListIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">SECURITY</span>
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white font-mono">
              {loading ? "..." : adminStats.totalSecurityLogs}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">
              Authentication Audit Events
            </div>
          </div>
        </div>
      )}

      {/* ── 3. RESELLER METRICS ROW ── */}
      {selectedRole === "reseller" && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
          <div
            onClick={() => navigate("/admin/users")}
            className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-xl bg-teal-100 dark:bg-teal-900/40 text-teal-600 dark:text-teal-400 flex items-center justify-center font-bold shadow-xs group-hover:scale-105 transition-transform">
                <UserGroupIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">NETWORK</span>
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white font-mono">
              {allAccounts.length || 0}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">
              Managed Sub-Clients
            </div>
          </div>

          <div
            onClick={() => navigate("/admin/ledgers")}
            className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold shadow-xs group-hover:scale-105 transition-transform">
                <WalletIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">RESELLER BAL</span>
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-emerald-600 dark:text-emerald-400 font-mono">
              ₹ {Number(tenantStats.walletCredits).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">
              Available Distribution Balance
            </div>
          </div>

          <div
            onClick={() => navigate("/admin/ledgers")}
            className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-xl bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold shadow-xs group-hover:scale-105 transition-transform">
                <CreditCardIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">ALLOCATED</span>
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-blue-600 dark:text-blue-400 font-mono">
              ₹ {Math.round(adminStats.totalCreditsPool).toLocaleString("en-IN")}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">
              Credits Distributed to Clients
            </div>
          </div>

          <div
            onClick={() => navigate("/workspace/campaigns")}
            className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-xl bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 flex items-center justify-center font-bold shadow-xs group-hover:scale-105 transition-transform">
                <MegaphoneIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">DISPATCH</span>
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-purple-600 dark:text-purple-400 font-mono">
              {tenantStats.totalCampaigns}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">
              Sub-Client Broadcast Runs
            </div>
          </div>

          <div
            onClick={() => navigate("/admin/phone-numbers")}
            className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-xl bg-cyan-100 dark:bg-cyan-900/40 text-cyan-600 dark:text-cyan-400 flex items-center justify-center font-bold shadow-xs group-hover:scale-105 transition-transform">
                <PhoneIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">CHANNELS</span>
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-cyan-600 dark:text-cyan-400 font-mono">
              {phoneNumbers.length}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">
              Active WhatsApp Senders
            </div>
          </div>
        </div>
      )}

      {/* ── 4. ACCOUNT METRICS ROW ── */}
      {selectedRole === "account" && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
          <div
            onClick={() => navigate("/admin/ledgers")}
            className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-xl bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold shadow-xs group-hover:scale-105 transition-transform">
                <WalletIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">BALANCE</span>
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-blue-600 dark:text-blue-400 font-mono">
              ₹ {Number(tenantStats.walletCredits).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">
              Available Wallet Credits
            </div>
          </div>

          <div
            onClick={() => navigate("/admin/phone-numbers")}
            className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold shadow-xs group-hover:scale-105 transition-transform">
                <PhoneIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">CHANNELS</span>
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-emerald-600 dark:text-emerald-400 font-mono">
              {phoneNumbers.length}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">
              Connected Phone Numbers
            </div>
          </div>

          <div
            onClick={() => navigate("/workspace/campaigns")}
            className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-xl bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 flex items-center justify-center font-bold shadow-xs group-hover:scale-105 transition-transform">
                <MegaphoneIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">BROADCASTS</span>
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-purple-600 dark:text-purple-400 font-mono">
              {tenantStats.totalCampaigns}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">
              Executed Campaigns
            </div>
          </div>

          <div
            onClick={() => navigate("/workspace/contacts")}
            className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-xl bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 flex items-center justify-center font-bold shadow-xs group-hover:scale-105 transition-transform">
                <UserGroupIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">AUDIENCE</span>
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-amber-600 dark:text-amber-400 font-mono">
              {tenantStats.totalRecipients.toLocaleString()}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">
              Mapped Audience Contacts
            </div>
          </div>

          <div
            onClick={() => navigate("/workspace/reports/campaigns")}
            className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-xl bg-teal-100 dark:bg-teal-900/40 text-teal-600 dark:text-teal-400 flex items-center justify-center font-bold shadow-xs group-hover:scale-105 transition-transform">
                <ChartBarIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">SUCCESS RATE</span>
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-teal-600 dark:text-teal-400 font-mono">
              {tenantStats.deliveryRate}%
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">
              Delivery Success Ratio
            </div>
          </div>
        </div>
      )}

      {/* ──────────────────────────────────────────────────────────────────────────
          QUICK ACTIONS SECTION
      ────────────────────────────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">
              Quick Actions & Shortcuts
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Direct shortcuts designed for your daily {selectedRole} workflow
            </p>
          </div>
        </div>

        {/* User Quick Actions (Marketer / arogya) */}
        {selectedRole === "user" && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3.5">
            <button
              onClick={() => navigate("/workspace/campaigns/create")}
              className="flex flex-col items-center justify-center p-4 rounded-xl bg-emerald-50/70 dark:bg-emerald-950/30 hover:bg-emerald-100 border border-emerald-200 dark:border-emerald-800/50 transition-all cursor-pointer group"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 text-white flex items-center justify-center shadow-md shadow-emerald-500/20 mb-2 group-hover:scale-105 transition-transform">
                <MegaphoneIcon className="w-5 h-5" />
              </div>
              <span className="text-xs font-bold text-gray-900 dark:text-white text-center">Create Campaign</span>
              <span className="text-[10px] text-emerald-700 dark:text-emerald-400 text-center mt-0.5">Launch broadcast</span>
            </button>

            <button
              onClick={() => navigate("/workspace/general-messages")}
              className="flex flex-col items-center justify-center p-4 rounded-xl bg-blue-50/70 dark:bg-blue-950/30 hover:bg-blue-100 border border-blue-200 dark:border-blue-800/50 transition-all cursor-pointer group"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center shadow-md shadow-blue-500/20 mb-2 group-hover:scale-105 transition-transform">
                <BoltIcon className="w-5 h-5" />
              </div>
              <span className="text-xs font-bold text-gray-900 dark:text-white text-center">General Messages</span>
              <span className="text-[10px] text-blue-700 dark:text-blue-400 text-center mt-0.5">Quick 1-page send</span>
            </button>

            <button
              onClick={() => navigate("/workspace/templates")}
              className="flex flex-col items-center justify-center p-4 rounded-xl bg-purple-50/70 dark:bg-purple-950/30 hover:bg-purple-100 border border-purple-200 dark:border-purple-800/50 transition-all cursor-pointer group"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 text-white flex items-center justify-center shadow-md shadow-purple-500/20 mb-2 group-hover:scale-105 transition-transform">
                <DocumentTextIcon className="w-5 h-5" />
              </div>
              <span className="text-xs font-bold text-gray-900 dark:text-white text-center">Message Templates</span>
              <span className="text-[10px] text-purple-700 dark:text-purple-400 text-center mt-0.5">Approved templates</span>
            </button>

            <button
              onClick={() => navigate("/workspace/contacts")}
              className="flex flex-col items-center justify-center p-4 rounded-xl bg-amber-50/70 dark:bg-amber-950/30 hover:bg-amber-100 border border-amber-200 dark:border-amber-800/50 transition-all cursor-pointer group"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white flex items-center justify-center shadow-md shadow-amber-500/20 mb-2 group-hover:scale-105 transition-transform">
                <UserGroupIcon className="w-5 h-5" />
              </div>
              <span className="text-xs font-bold text-gray-900 dark:text-white text-center">Contacts & Groups</span>
              <span className="text-[10px] text-amber-700 dark:text-amber-400 text-center mt-0.5">Audience lists</span>
            </button>

            <button
              onClick={() => navigate("/workspace/reports/campaigns")}
              className="flex flex-col items-center justify-center p-4 rounded-xl bg-cyan-50/70 dark:bg-cyan-950/30 hover:bg-cyan-100 border border-cyan-200 dark:border-cyan-800/50 transition-all cursor-pointer group"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-600 text-white flex items-center justify-center shadow-md shadow-cyan-500/20 mb-2 group-hover:scale-105 transition-transform">
                <ChartBarIcon className="w-5 h-5" />
              </div>
              <span className="text-xs font-bold text-gray-900 dark:text-white text-center">Delivery Reports</span>
              <span className="text-[10px] text-cyan-700 dark:text-cyan-400 text-center mt-0.5">Sent, read & failed</span>
            </button>

            <button
              onClick={() => navigate("/workspace/messages")}
              className="flex flex-col items-center justify-center p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 hover:bg-slate-100 border border-slate-200 dark:border-slate-700 transition-all cursor-pointer group"
            >
              <div className="w-10 h-10 rounded-xl bg-slate-700 text-white flex items-center justify-center shadow-md mb-2 group-hover:scale-105 transition-transform">
                <ClipboardDocumentListIcon className="w-5 h-5" />
              </div>
              <span className="text-xs font-bold text-gray-900 dark:text-white text-center">Message Audit</span>
              <span className="text-[10px] text-slate-600 dark:text-slate-400 text-center mt-0.5">Raw dispatch logs</span>
            </button>
          </div>
        )}

        {/* Admin Quick Actions */}
        {selectedRole === "admin" && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3.5">
            <button
              onClick={() => navigate("/admin/users/create")}
              className="flex flex-col items-center justify-center p-4 rounded-xl bg-blue-50/60 dark:bg-blue-900/20 hover:bg-blue-100/80 border border-blue-100 dark:border-blue-800/40 transition-all cursor-pointer group"
            >
              <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-sm mb-2 group-hover:scale-105 transition-transform">
                <PlusIcon className="w-5 h-5" />
              </div>
              <span className="text-xs font-bold text-gray-900 dark:text-white text-center">Add New User</span>
            </button>

            <button
              onClick={() => navigate("/admin/users")}
              className="flex flex-col items-center justify-center p-4 rounded-xl bg-emerald-50/60 dark:bg-emerald-900/20 hover:bg-emerald-100/80 border border-emerald-100 dark:border-emerald-800/40 transition-all cursor-pointer group"
            >
              <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center shadow-sm mb-2 group-hover:scale-105 transition-transform">
                <UsersIcon className="w-5 h-5" />
              </div>
              <span className="text-xs font-bold text-gray-900 dark:text-white text-center">User Management</span>
            </button>

            <button
              onClick={() => navigate("/admin/operators")}
              className="flex flex-col items-center justify-center p-4 rounded-xl bg-cyan-50/60 dark:bg-cyan-900/20 hover:bg-cyan-100/80 border border-cyan-100 dark:border-cyan-800/40 transition-all cursor-pointer group"
            >
              <div className="w-10 h-10 rounded-xl bg-cyan-600 text-white flex items-center justify-center shadow-sm mb-2 group-hover:scale-105 transition-transform">
                <SignalIcon className="w-5 h-5" />
              </div>
              <span className="text-xs font-bold text-gray-900 dark:text-white text-center">Telco Carriers</span>
            </button>

            <button
              onClick={() => navigate("/admin/databases")}
              className="flex flex-col items-center justify-center p-4 rounded-xl bg-amber-50/60 dark:bg-amber-900/20 hover:bg-amber-100/80 border border-amber-100 dark:border-amber-800/40 transition-all cursor-pointer group"
            >
              <div className="w-10 h-10 rounded-xl bg-amber-600 text-white flex items-center justify-center shadow-sm mb-2 group-hover:scale-105 transition-transform">
                <CircleStackIcon className="w-5 h-5" />
              </div>
              <span className="text-xs font-bold text-gray-900 dark:text-white text-center">Databases</span>
            </button>

            <button
              onClick={() => navigate("/admin/ledgers")}
              className="flex flex-col items-center justify-center p-4 rounded-xl bg-purple-50/60 dark:bg-purple-900/20 hover:bg-purple-100/80 border border-purple-100 dark:border-purple-800/40 transition-all cursor-pointer group"
            >
              <div className="w-10 h-10 rounded-xl bg-purple-600 text-white flex items-center justify-center shadow-sm mb-2 group-hover:scale-105 transition-transform">
                <CreditCardIcon className="w-5 h-5" />
              </div>
              <span className="text-xs font-bold text-gray-900 dark:text-white text-center">Ledgers & Credits</span>
            </button>

            <button
              onClick={() => navigate("/admin/login-logs")}
              className="flex flex-col items-center justify-center p-4 rounded-xl bg-slate-100 dark:bg-slate-800/60 hover:bg-slate-200/80 border border-slate-200 dark:border-slate-700 transition-all cursor-pointer group"
            >
              <div className="w-10 h-10 rounded-xl bg-slate-700 text-white flex items-center justify-center shadow-sm mb-2 group-hover:scale-105 transition-transform">
                <ClipboardDocumentListIcon className="w-5 h-5" />
              </div>
              <span className="text-xs font-bold text-gray-900 dark:text-white text-center">Security Logs</span>
            </button>
          </div>
        )}
      </div>

      {/* ──────────────────────────────────────────────────────────────────────────
          MAIN CONTENT GRIDS: 2-COLUMN SPLIT
      ────────────────────────────────────────────────────────────────────────── */}

      {/* ── FOR USER / ACCOUNT VIEW (Client Workspace) ── */}
      {(selectedRole === "user" || selectedRole === "account") && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column (2 Cols): Credit Cut Graph & Recent Campaigns */}
          <div className="lg:col-span-2 space-y-6">
            {/* 1. INTERACTIVE GRAPH: Messages Sent & Credit Cut */}
            <CreditAndMessageGraph
              campaigns={recentCampaigns}
              debits={debitEntries}
              walletBalance={tenantStats.walletCredits}
            />

            {/* 2. Recent Broadcast Campaigns Feed */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white">
                    Recent Broadcast Campaigns
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Latest automated WhatsApp dispatches from your workspace
                  </p>
                </div>
                <button
                  onClick={() => navigate("/workspace/campaigns")}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
                >
                  <span>View all campaigns</span>
                  <ArrowRightIcon className="w-3.5 h-3.5" />
                </button>
              </div>

              {loading ? (
                <div className="py-12 text-center">
                  <ArrowPathIcon className="w-6 h-6 mx-auto text-blue-500 animate-spin mb-2" />
                  <p className="text-xs text-gray-400">Loading campaign dispatches...</p>
                </div>
              ) : recentCampaigns.length === 0 ? (
                <div className="py-12 px-4 text-center border border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
                  <MegaphoneIcon className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">No campaigns sent yet</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 max-w-sm mx-auto mt-1 mb-4">
                    Ready to engage your audience? Launch a targeted broadcast or send a quick message in seconds.
                  </p>
                  <button
                    onClick={() => navigate("/workspace/campaigns/create")}
                    className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-600 to-blue-600 text-white rounded-xl text-xs font-semibold shadow-sm cursor-pointer"
                  >
                    <PlusIcon className="w-4 h-4 mr-1.5" />
                    Create First Campaign
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentCampaigns.map((camp, idx) => {
                    const recipients = Number(camp.int_recipient_count || 0);
                    const sent = Number(camp.int_sent_count || (camp.str_status === "COMPLETED" ? recipients : 0));
                    const progressPct = recipients > 0 ? Math.min(100, Math.round((sent / recipients) * 100)) : 100;
                    const isDone = camp.str_status === "COMPLETED" || progressPct === 100;

                    return (
                      <div
                        key={camp._id || idx}
                        className="p-4 rounded-xl border border-gray-150 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-700/30 hover:bg-gray-100/60 dark:hover:bg-gray-700/50 transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-bold text-sm text-gray-900 dark:text-white truncate">
                              {camp.str_campaign_name || `Broadcast #${idx + 1}`}
                            </h4>
                            <span
                              className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                isDone
                                  ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300"
                                  : camp.str_status === "PROCESSING" || camp.str_status === "SENDING"
                                  ? "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 animate-pulse"
                                  : "bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                              }`}
                            >
                              {camp.str_status || "COMPLETED"}
                            </span>
                          </div>

                          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-1">
                            <span className="font-mono">
                              {recipients.toLocaleString()} recipients
                            </span>
                            <span>•</span>
                            <span className="truncate">
                              Template: {camp.str_template_name || "General Message"}
                            </span>
                            {Array.isArray(camp.arr_sender_numbers) && camp.arr_sender_numbers.length > 1 ? (
                              <>
                                <span>•</span>
                                <span className="text-indigo-600 dark:text-indigo-400 font-semibold font-mono">
                                  {camp.arr_sender_numbers.length} Senders ({camp.str_sender_distribution_mode === "DIFFERENT" ? "Diff" : "Same"})
                                </span>
                              </>
                            ) : (camp.str_sender_number || camp.str_phone_number_id) ? (
                              <>
                                <span>•</span>
                                <span className="font-mono text-gray-600 dark:text-gray-300">
                                  +{String(camp.str_sender_number || camp.str_phone_number_id).replace(/^\+/, "")}
                                </span>
                              </>
                            ) : null}
                            {camp.dbl_total_cost > 0 && (
                              <>
                                <span>•</span>
                                <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                                  Cost: ₹ {Number(camp.dbl_total_cost).toFixed(2)}
                                </span>
                              </>
                            )}
                          </div>

                          {/* Visual Progress Bar */}
                          <div className="mt-2.5 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
                            <div
                              className={`h-full transition-all duration-500 ${
                                isDone ? "bg-emerald-500" : "bg-blue-600"
                              }`}
                              style={{ width: `${progressPct}%` }}
                            />
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0 self-end sm:self-center">
                          <button
                            onClick={() => navigate("/workspace/reports/campaigns")}
                            className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 text-xs font-semibold transition-colors cursor-pointer"
                          >
                            View Report
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right Column (1 Col): Delivery Health Ratio & Recent Credit Deductions */}
          <div className="space-y-6">
            {/* 1. Delivery Quality Meter Card (REAL BOUNDED DATA!) */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                Delivery Health & Ratio
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                Real-time delivery verification across WhatsApp carriers
              </p>

              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="font-medium text-gray-600 dark:text-gray-400">Delivered Rate</span>
                    <span className="font-bold text-emerald-600">{tenantStats.deliveryRate}%</span>
                  </div>
                  <div className="w-full bg-gray-100 dark:bg-gray-700 h-2.5 rounded-full overflow-hidden">
                    <div
                      className="bg-emerald-500 h-full rounded-full transition-all duration-500"
                      style={{ width: `${tenantStats.deliveryRate}%` }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="font-medium text-gray-600 dark:text-gray-400">Read & Engaged</span>
                    <span className="font-bold text-blue-600">{tenantStats.readRate}%</span>
                  </div>
                  <div className="w-full bg-gray-100 dark:bg-gray-700 h-2.5 rounded-full overflow-hidden">
                    <div
                      className="bg-blue-600 h-full rounded-full transition-all duration-500"
                      style={{ width: `${tenantStats.readRate}%` }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="font-medium text-gray-600 dark:text-gray-400">Failed / Invalid</span>
                    <span className="font-bold text-rose-500">{tenantStats.failedRate}%</span>
                  </div>
                  <div className="w-full bg-gray-100 dark:bg-gray-700 h-2.5 rounded-full overflow-hidden">
                    <div
                      className="bg-rose-500 h-full rounded-full transition-all duration-500"
                      style={{ width: `${tenantStats.failedRate}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Recent Credit Deductions (Debit Ledger) */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white">
                    Recent Credit Deductions
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Latest broadcast usage debited from your wallet
                  </p>
                </div>
                <button
                  onClick={() => navigate("/admin/ledgers")}
                  className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
                >
                  Ledger →
                </button>
              </div>

              {debitEntries.length === 0 ? (
                <p className="text-xs text-gray-400 py-6 text-center">
                  No credit deductions recorded yet.
                </p>
              ) : (
                <div className="space-y-2.5">
                  {debitEntries.slice(0, 4).map((entry, idx) => (
                    <div
                      key={entry._id || idx}
                      className="p-3 rounded-xl bg-gray-50/70 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-700/60 flex items-center justify-between"
                    >
                      <div className="min-w-0 flex-1 pr-2">
                        <span className="font-semibold text-xs text-gray-900 dark:text-white truncate block">
                          {entry.str_description || "Campaign Dispatch"}
                        </span>
                        <span className="text-[10px] text-gray-400 font-mono block mt-0.5">
                          {entry.dttm_created_on
                            ? new Date(entry.dttm_created_on).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "Recent"}
                        </span>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className="text-xs font-bold font-mono text-rose-600 dark:text-rose-400">
                          - ₹ {Number(entry.dbl_amount || 0).toFixed(2)}
                        </span>
                        <span className="text-[10px] text-gray-400 block font-mono">
                          Bal: ₹ {Number(entry.dbl_new_balance || 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 3. Connected Sender Phone Numbers */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                  Connected WhatsApp Senders
                </h3>
                <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded-full">
                  {phoneNumbers.length} Online
                </span>
              </div>
              <div className="space-y-2">
                {phoneNumbers.length === 0 ? (
                  <p className="text-xs text-gray-400 py-4 text-center">
                    No phone numbers provisioned yet.
                  </p>
                ) : (
                  phoneNumbers.slice(0, 3).map((p, idx) => (
                    <div
                      key={p._id || idx}
                      className="p-3 rounded-xl bg-gray-50/70 dark:bg-gray-700/40 border border-gray-150 dark:border-gray-700 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-emerald-600 text-white flex items-center justify-center text-xs font-bold">
                          <PhoneIcon className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <span className="font-mono font-bold text-xs text-gray-900 dark:text-white block">
                            +{p.str_phone_number || "919574780202"}
                          </span>
                          <span className="text-[10px] text-gray-400">
                            WABA: {p.str_waba_id ? `${String(p.str_waba_id).substring(0, 8)}...` : "Active"}
                          </span>
                        </div>
                      </div>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                        VERIFIED
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── FOR ADMIN / RESELLER VIEW (Platform Overview - SuperAdmin Only) ── */}
      {selectedRole === "admin" && isSuper && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Recent Registered Users */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                  Recent Platform Users
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Newest verified platform user accounts
                </p>
              </div>
              <button
                onClick={() => navigate("/admin/users")}
                className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
              >
                <span>View all</span>
                <ArrowRightIcon className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="space-y-2.5">
              {recentUsers.length === 0 ? (
                <p className="text-xs text-gray-400 py-6 text-center">No accounts registered yet.</p>
              ) : (
                recentUsers.map((u, idx) => (
                  <div
                    key={u._id || idx}
                    className="flex items-center justify-between p-3 rounded-xl bg-gray-50/70 dark:bg-gray-700/40 hover:bg-gray-100/70 dark:hover:bg-gray-700/60 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white font-bold flex items-center justify-center text-xs flex-shrink-0 shadow-xs">
                        {(u.str_name || u.str_username || "U").charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-xs font-bold text-gray-900 dark:text-white truncate">
                          {u.str_name || u.str_username}
                        </h4>
                        <p className="text-[11px] text-gray-400 truncate">
                          @{u.str_username} • {u.str_email}
                        </p>
                      </div>
                    </div>

                    <span
                      className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                        (u.str_status || "active").toLowerCase() === "active"
                          ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300"
                          : "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300"
                      }`}
                    >
                      {u.str_status || "ACTIVE"}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right: Security & Authentication Logs */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                  Security & Login Activity
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Audit events & 2FA authentication logs
                </p>
              </div>
              <button
                onClick={() => navigate("/admin/login-logs")}
                className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
              >
                <span>View all</span>
                <ArrowRightIcon className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="space-y-2.5">
              {recentSecurityLogs.length === 0 ? (
                <p className="text-xs text-gray-400 py-6 text-center">No authentication attempts recorded yet.</p>
              ) : (
                recentSecurityLogs.map((log, index) => (
                  <div
                    key={log._id || index}
                    className="flex items-center justify-between p-3 rounded-xl bg-gray-50/70 dark:bg-gray-700/40 hover:bg-gray-100/70 dark:hover:bg-gray-700/60 transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="text-xs font-bold text-gray-900 dark:text-white font-mono">
                          @{log.str_username}
                        </h4>
                        <span className="text-[10px] text-gray-400 font-mono">
                          {log.dttm_login_attempted_on
                            ? new Date(log.dttm_login_attempted_on).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                            : "Just now"}
                        </span>
                      </div>
                      <p className="text-[10px] text-gray-400 font-mono truncate mt-0.5">
                        IP: {log.str_ip_address || "127.0.0.1"}
                      </p>
                    </div>

                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider ${
                        log.bool_is_login
                          ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300"
                          : "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300"
                      }`}
                    >
                      {log.bool_is_login ? "SUCCESS" : "FAILED"}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
